function multiplyCartNotificationStars(addedQuantity) {
  // Manages star signage related screen reader notifications on mini cart
  if (!addedQuantity || addedQuantity <= 1) return;

  const productContainer = document.getElementById('cart-notification-product');
  if (!productContainer) return;

  const signage = productContainer.querySelector('.stars-signage');
  const priceElement = productContainer.querySelector('.cart-item-price');
  if (signage && typeof updateStarSignage === 'function') {
    updateStarSignage(signage, addedQuantity, priceElement);
  }
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Setup selling plan handling
        this.setupSellingPlanHandlers();

        this.translations = document.querySelector('[data-subscription-translations]')
          ? JSON.parse(document.querySelector('[data-subscription-translations]').textContent)
          : {};
      }

      setupSellingPlanHandlers() {
        // Find selling plan inputs
        this.subscriptionModule = this.closest('product-info')?.querySelector('subscription-module');
        this.sellingPlanInput = this.form.querySelector('[name="selling_plan"]');

        if (this.subscriptionModule) {
          this.subscriptionModule.addEventListener('selling-plan:change', this.handleSellingPlanChange.bind(this));
        }
      }

      handleSellingPlanChange(event) {
        if (!this.sellingPlanInput) return;

        // Update the selling plan input value
        this.sellingPlanInput.value = event.detail.value;
      }

      async onSubmitHandler(evt) {
        if (this.dataset.hasPersonalization === 'true') return;
        evt.preventDefault();

        const guestLock = this.form.querySelector('.product-form__guest-lock');
        if (guestLock && guestLock.offsetParent !== null) return;

        if (this.submitButton.classList.contains('button--is-restricted')) {
          // Announce the restriction message to screen readers
          const messageEl = this.form.querySelector('#cart-max-quantity-message');
          if (!messageEl) return;

          messageEl.setAttribute('tabindex', '-1');
          messageEl.focus();
          setTimeout(() => messageEl.removeAttribute('tabindex'), 1000);

          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Check if this is a subscription product
        const subscriptionModule = this.closest('product-info')?.querySelector('subscription-module');
        const isSubscription = subscriptionModule?.isSubscriptionSelected();
        const shouldBlockAfterAdd = isSubscription && !subscriptionModule.validateSubscriptionQuantity();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                    multiplyCartNotificationStars(parseInt(formData.get('quantity')));
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
              multiplyCartNotificationStars(parseInt(formData.get('quantity')));
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !shouldBlockAfterAdd) {
              this.submitButton.removeAttribute('aria-disabled');
            } else if (shouldBlockAfterAdd) {
              this.submitButton.classList.add('button--is-restricted');
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.setAttribute('aria-describedby', 'cart-max-quantity-message');
              this.form.querySelector('#cart-max-quantity-message')?.removeAttribute('aria-hidden');

              if (this.submitButtonText) {
                this.submitButtonText.textContent = this.translations.maximum_quantity_in_cart;
              }
            }
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');

          // sellingPlanInput has value means "subscription"
          if (this.sellingPlanInput.value) {
            this.submitButtonText.textContent = this.translations.add_subscription_to_cart;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      togglePreorderButton(isPreorder = false) {
        if (isPreorder) {
          this.submitButton.classList.add('preorder');
          this.submitButtonText.textContent = window.variantStrings.preorder;
        } else {
          this.submitButton.classList.remove('preorder');
        }
      }
    }
  );
}
